import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Sparkles, ChefHat, ListChecks, Tag, Wifi, WifiOff } from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { ROUTE_PATHS, MealPlan } from '@/lib/index';
import { DEMO_MEAL_PLAN } from '@/data/demoMealPlan';
import { getDemoMealPlan } from '@/data/multiStoreDemoData';
import { springPresets } from '@/lib/motion';

const EDGE_FUNCTION_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan';
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.PDtIhTOuZNnwOqNkdOD8eTC6SCXdlPT5H7vrcU30UXk';

const STEPS = [
  { icon: Tag,          label: "Fetching this week's live flyer deals…" },
  { icon: ShoppingCart, label: 'Filtering on-sale cooking ingredients…' },
  { icon: Sparkles,     label: 'AI is crafting your meal plan…' },
  { icon: ChefHat,      label: 'Writing step-by-step recipes…' },
  { icon: ListChecks,   label: 'Calculating costs & building grocery list…' },
];

const DEMO_CHAINS = ['Sobeys', 'Walmart', 'Metro', 'FreshCo', 'No Frills'];

export default function ScanningPage() {
  const { postalCode, setMealPlan, setStep, cartMeals } = useAppStore();
  const navigate   = useNavigate();
  const [params]   = useSearchParams();
  const postal     = params.get('postal')   || postalCode || 'M5V3A8';
  const merchant   = params.get('merchant') || '';

  const [stepIdx, setStepIdx]     = useState(0);
  const [done, setDone]           = useState(false);
  const [source, setSource]       = useState<'live' | 'demo' | null>(null);
  const [storeName, setStoreName] = useState(merchant || '');
  const [dealCount, setDealCount] = useState(0);

  const finalize = (plan: MealPlan) => {
    setMealPlan(plan);
    setStep('mealplan');
    setDone(true);
    setTimeout(() => navigate(ROUTE_PATHS.MEAL_PLAN), 700);
  };

  const loadDemo = () => {
    const hash  = postal.charCodeAt(0) + (postal.charCodeAt(2) || 0);
    const chain = merchant || DEMO_CHAINS[hash % DEMO_CHAINS.length];
    const plan  = chain === 'No Frills'
      ? { ...DEMO_MEAL_PLAN, store_name: 'No Frills', demo_mode: true }
      : { ...getDemoMealPlan(chain), store_name: chain, demo_mode: true };
    setStoreName(chain);
    setSource('demo');
    return plan as MealPlan;
  };

  useEffect(() => {
    let current  = 0;
    let resolved = false;
    let resolvedPlan: MealPlan | null = null;

    const advanceStep = () => {
      current += 1;
      if (current < STEPS.length) {
        setStepIdx(current);
        setTimeout(advanceStep, 900);
      } else if (resolved && resolvedPlan) {
        finalize(resolvedPlan);
      }
    };
    setTimeout(advanceStep, 900);

    const loadPlan = async () => {
      try {
        const res = await fetch(EDGE_FUNCTION_URL, {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${ANON_KEY}`,
            'apikey': ANON_KEY,
          },
          body: JSON.stringify({
            action: 'mealplan', postalCode: postal, merchant,
            // Tell AI which recipes are already in cart so it generates different ones
            avoidRecipes: cartMeals.map(m => m.name),
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const plan = await res.json() as MealPlan;
        if (!plan.meals?.length) throw new Error('Empty meal plan returned');
        setSource('live');
        setStoreName(plan.store_name);
        setDealCount(plan.sale_items_found ?? 0);
        resolvedPlan = plan;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = /rate.limit|rate_limit|429|exhausted|tokens.*day/i.test(msg);
        console.warn('Edge fn failed:', msg);
        if (isRateLimit) {
          // Don't silently show demo — show the error so user knows what's happening
          setSource('demo');
          const plan = loadDemo();
          (plan as unknown as Record<string,unknown>)._rateLimit = true;
          resolvedPlan = plan;
        } else {
          resolvedPlan = loadDemo();
        }
      }
      resolved = true;
      if (current >= STEPS.length) finalize(resolvedPlan!);
    };

    loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={springPresets.gentle}
        className="text-center max-w-sm w-full"
      >
        <div className="relative w-24 h-24 mx-auto mb-8">
          <motion.div className="absolute inset-0 rounded-full border-4 border-primary/20"
            animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
          <motion.div className="absolute inset-2 rounded-full border-4 border-t-primary border-r-transparent border-b-transparent border-l-transparent"
            animate={{ rotate: -360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.span key="done" initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="text-3xl text-primary font-bold">✓</motion.span>
              ) : (
                <motion.span key={stepIdx}
                  initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                  {(() => { const S = STEPS[stepIdx].icon; return <S className="w-7 h-7 text-primary" />; })()}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        <h1 className="text-xl font-bold text-foreground mb-1">
          {done ? '🎉 Meal Plan Ready!' : 'Building your meal plan…'}
        </h1>
        <p className="text-sm text-muted-foreground mb-2 font-mono">{postal}</p>

        <AnimatePresence>
          {source && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center mb-3">
              {source === 'live' ? (
                <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full">
                  <Wifi className="w-2.5 h-2.5" />
                  {dealCount > 0 ? `${dealCount} live deals` : 'Live data'} · {storeName}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] bg-yellow-100 text-yellow-700 font-semibold px-2.5 py-1 rounded-full">
                  <WifiOff className="w-2.5 h-2.5" /> Sample data · {storeName}
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.p key={stepIdx}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
            className="text-sm text-muted-foreground min-h-[20px]">
            {STEPS[Math.min(stepIdx, STEPS.length - 1)].label}
          </motion.p>
        </AnimatePresence>

        <div className="flex items-center justify-center gap-1.5 mt-6">
          {STEPS.map((_, i) => (
            <div key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${i <= stepIdx ? 'bg-primary w-4' : 'bg-muted w-1.5'}`} />
          ))}
        </div>

        <p className="text-[10px] text-muted-foreground mt-8 opacity-50">
          Powered by Flipp.com + Groq AI · Real flyer data
        </p>
      </motion.div>
    </div>
  );
}
